/**
 * Lift2Fit — coaching workspace: insights (`#/crm/insights`).
 *
 * The premium layer: the deterministic evaluation matrix read across the whole
 * roster, plus the roster-derived facts, with the evidence and a suggested next
 * step for every flag. No LLM is called from the client — the API computes this
 * and this screen only explains it.
 *
 * Grouped two ways (by client and by severity) so a coach can work either from
 * the person or from the most urgent signal.
 */
import { useMemo, useState } from 'react';
import { api, type CrmClientInsight, type CrmInsightFlag, type CrmInsightSeverity } from '@/lib/api';
import { cn, formatCompactCount, formatCount } from '@/lib/format';
import { AlertIcon, ArrowRightIcon, CheckIcon, SparkIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  MetricTile,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { useAsync, useCachedAsync } from '../hooks';
import { Link } from '../router';
import {
  CRM_EYEBROW,
  crmFlagLabel,
  insightSourceLabel,
  severityTone,
  severityWord,
  verdictMeta,
} from './shared';

const SEVERITY_ORDER: CrmInsightSeverity[] = ['danger', 'warning', 'info'];

const SEVERITY_HEADINGS: Record<CrmInsightSeverity, string> = {
  danger: 'Action now',
  warning: 'Watch',
  info: 'Worth a note',
};

function FlagCard({ client, flag }: { client: CrmClientInsight; flag: CrmInsightFlag }) {
  return (
    <li
      data-testid={`crm-insight-flag-${client.clientId}-${flag.code}`}
      className="flex flex-col gap-2 border border-hairline bg-surface p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <StatusChip tone={severityTone(flag.severity)} icon={<AlertIcon />}>
          {severityWord(flag.severity)}
        </StatusChip>
        <span className="text-[14px] font-bold text-ink">{crmFlagLabel(flag.code)}</span>
        <StatusChip tone="neutral">{insightSourceLabel(flag.source)}</StatusChip>
        <Link
          to={`/crm/clients/${client.clientId}`}
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.08em] text-accent hover:text-accent-hover"
        >
          {client.displayName}
          <ArrowRightIcon />
        </Link>
      </div>
      <p className="text-[13px] text-ink" data-testid={`crm-insight-evidence-${client.clientId}-${flag.code}`}>
        {flag.evidence}
      </p>
      <p className="text-[13px] text-ink-muted">
        <span className="font-bold uppercase tracking-[0.06em] text-accent">Suggested action: </span>
        {flag.suggestedAction}
      </p>
      {Object.keys(flag.evidenceData).length ? (
        <details className="text-[12px] text-ink-muted">
          <summary className="cursor-pointer font-bold uppercase tracking-[0.06em]">Evidence data</summary>
          <dl className="mt-1 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
            {Object.entries(flag.evidenceData).map(([key, value]) => (
              <div key={key} className="flex items-baseline justify-between gap-2 border-b border-hairline py-0.5">
                <dt>{key}</dt>
                <dd className="l2f-tabular font-mono text-ink">{String(value)}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </li>
  );
}

function ClientCard({ client }: { client: CrmClientInsight }) {
  const verdict = verdictMeta(client.verdict);
  return (
    <li
      data-testid={`crm-insight-client-${client.clientId}`}
      className="flex flex-col gap-3 border border-hairline bg-page-subtle p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="flex flex-wrap items-center gap-2 text-[15px] font-bold text-ink">
            {client.displayName}
            <StatusChip tone={verdict.tone}>{verdict.label}</StatusChip>
            <StatusChip tone={client.flags.length ? 'warning' : 'success'}>
              {client.flags.length} flag(s)
            </StatusChip>
          </p>
          <p className="text-[13px] text-ink-muted">{client.headline}</p>
        </div>
        <Link
          to={`/crm/clients/${client.clientId}`}
          className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
        >
          Open client
          <ArrowRightIcon />
        </Link>
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="flex flex-col gap-0.5">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Sessions analysed</dt>
          <dd className="l2f-tabular font-mono text-[14px] font-bold text-ink">{client.metrics.sessionsAnalyzed}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Compliance</dt>
          <dd className="l2f-tabular font-mono text-[14px] font-bold text-ink">{client.metrics.compliancePct.toFixed(1)}%</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Avg RPE</dt>
          <dd className="l2f-tabular font-mono text-[14px] font-bold text-ink">{client.metrics.avgRpe.toFixed(1)}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">PRs</dt>
          <dd className="l2f-tabular font-mono text-[14px] font-bold text-ink">{client.metrics.prCount}</dd>
        </div>
      </dl>

      {client.flags.length ? (
        <ul className="flex flex-col gap-2">{client.flags.map((flag) => <FlagCard key={flag.code} client={client} flag={flag} />)}</ul>
      ) : (
        <p className="flex items-center gap-2 text-[13px] text-success">
          <CheckIcon />
          Nothing flagged in the current window.
        </p>
      )}

      {client.verdictReasons.length ? (
        <details className="text-[12px] text-ink-muted">
          <summary className="cursor-pointer font-bold uppercase tracking-[0.06em]">Why the matrix said this</summary>
          <ul className="mt-1 flex list-disc flex-col gap-1 pl-5">
            {client.verdictReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </li>
  );
}

export function CrmInsightsScreen() {
  const state = useCachedAsync('crm/insights', () => api.crmInsights(), []);
  const [group, setGroup] = useState<'client' | 'severity'>('client');
  const insights = state.data;

  const clients = useMemo(() => {
    const rows = insights?.data ?? [];
    return [...rows].sort(
      (a, b) => b.flags.length - a.flags.length || a.displayName.localeCompare(b.displayName),
    );
  }, [insights]);

  const bySeverity = useMemo(() => {
    const buckets: Record<CrmInsightSeverity, Array<{ client: CrmClientInsight; flag: CrmInsightFlag }>> = {
      danger: [],
      warning: [],
      info: [],
    };
    for (const client of clients) {
      for (const flag of client.flags) buckets[flag.severity].push({ client, flag });
    }
    return buckets;
  }, [clients]);

  const byCode = Object.entries(insights?.totals.byCode ?? {}).sort((a, b) => b[1] - a[1]);

  if (state.loading && !insights) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Reading the roster through the matrix" rows={5} />
      </div>
    );
  }

  if (!insights) {
    return (
      <EmptyState
        title="We could not read the insights"
        body={state.error ?? 'The API did not answer. Check that you are signed in to a coaching workspace.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/crm" variant="outline">
            Back to the summary
          </AppLinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="crm-insights">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title="Insights"
        badge={<StatusChip tone="accent">Deterministic · no LLM</StatusChip>}
        lead={`Every active client read through the same evaluation matrix that powers the lifter's AI coach, over the last ${insights.windowDays} days — with the evidence behind each flag.`}
        actions={
          <AppLinkButton to="/crm/clients" variant="outline">
            Open clients
          </AppLinkButton>
        }
      />

      <section aria-label="Insight totals" data-testid="crm-insights-totals" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricTile
          label="Clients read"
          value={formatCompactCount(insights.totals.clients)}
          valueTitle={formatCount(insights.totals.clients)}
          hint="active links"
        />
        <MetricTile
          label="Flagged"
          value={formatCompactCount(insights.totals.flagged)}
          valueTitle={formatCount(insights.totals.flagged)}
          hint={insights.totals.flagged ? 'need a decision' : 'nothing flagged'}
          hintTone={insights.totals.flagged ? 'danger' : 'success'}
          icon={insights.totals.flagged ? <AlertIcon /> : <CheckIcon />}
        />
        <MetricTile label="Flag types" value={formatCompactCount(byCode.length)} valueTitle={formatCount(byCode.length)} hint="distinct rules firing" />
        <MetricTile
          label="Most common"
          value={byCode[0] ? crmFlagLabel(byCode[0][0]) : '—'}
          hint={byCode[0] ? `${byCode[0][1]} client(s)` : 'no rule firing'}
        />
      </section>

      {byCode.length ? (
        <Panel title="Flags by code" subtitle="Every rule that fired across the roster, most common first.">
          <ul className="flex flex-wrap gap-2">
            {byCode.map(([code, count]) => (
              <li key={code}>
                <StatusChip tone="neutral">
                  {crmFlagLabel(code)} · {count}
                </StatusChip>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Group insights">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Group by</span>
        <button
          type="button"
          aria-pressed={group === 'client'}
          data-testid="crm-insights-by-client"
          onClick={() => setGroup('client')}
          className={cn(
            'inline-flex h-9 items-center border px-3 text-[11px] font-bold uppercase tracking-[0.08em]',
            group === 'client' ? 'border-ink bg-ink text-ink-inverse' : 'border-hairline bg-surface text-ink hover:border-accent',
          )}
        >
          Client
        </button>
        <button
          type="button"
          aria-pressed={group === 'severity'}
          data-testid="crm-insights-by-severity"
          onClick={() => setGroup('severity')}
          className={cn(
            'inline-flex h-9 items-center border px-3 text-[11px] font-bold uppercase tracking-[0.08em]',
            group === 'severity' ? 'border-ink bg-ink text-ink-inverse' : 'border-hairline bg-surface text-ink hover:border-accent',
          )}
        >
          Severity
        </button>
      </div>

      {clients.length === 0 ? (
        <div data-testid="crm-insights-empty">
          <EmptyState
            title="No clients to read"
            body="Insights appear once a client is linked. Invite a client and the matrix starts reading their shared training."
            icon={<SparkIcon className="h-8 w-8 text-accent" />}
            action={
              <AppLinkButton to="/crm/clients" variant="outline">
                Open clients
              </AppLinkButton>
            }
          />
        </div>
      ) : group === 'client' ? (
        <Panel title="By client" subtitle="Who to look at, and exactly why." bodyClassName="gap-4">
          <ul className="flex flex-col gap-3">
            {clients.map((client) => (
              <ClientCard key={client.clientId} client={client} />
            ))}
          </ul>
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          {SEVERITY_ORDER.map((severity) => {
            const entries = bySeverity[severity];
            return (
              <Panel
                key={severity}
                testId={`crm-insights-severity-${severity}`}
                title={SEVERITY_HEADINGS[severity]}
                subtitle={`${entries.length} flag(s) at this severity.`}
              >
                {entries.length ? (
                  <ul className="flex flex-col gap-2">
                    {entries.map(({ client, flag }) => (
                      <FlagCard key={`${client.clientId}-${flag.code}`} client={client} flag={flag} />
                    ))}
                  </ul>
                ) : (
                  <p className="text-[13px] text-ink-muted">Nothing at this severity.</p>
                )}
              </Panel>
            );
          })}
        </div>
      )}

      <p className="text-[12px] text-ink-muted">
        Flags are computed deterministically by the API from the evaluation matrix and the roster aggregates. This screen
        makes no model call and nothing here is generated prose.
      </p>
    </div>
  );
}
